/**
 * How many of a bulk action's requests are out at once.
 *
 * The board's bulk actions are one request per ticket, and "select all" on a
 * large board fired every one of them at the same moment. Each takes a
 * connection from the API's pool, which is small (16 in production) and has a
 * history of turning ticket requests from under two seconds into twelve when it
 * is crowded - for everyone in the workspace, not just the person who pressed
 * the button. Six is what a browser would hold open to one host anyway.
 */
export const BULK_REQUEST_LIMIT = 6;

/**
 * `Promise.allSettled` with at most `limit` tasks running at once.
 *
 * Results come back in the order of `items`, whatever order the tasks finish
 * in, so a caller can match each one to what it was for. A task that throws,
 * even before returning a promise, is a rejected result rather than an error
 * out of here, which is the whole point of settling.
 */
export async function settleWithLimit<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        results[index] = { status: 'fulfilled', value: await task(items[index]!) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return results;
}
